import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, ArrowUpRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Input } from "@/platform/ui/input";
import { Label } from "@/platform/ui/label";
import {
  signInSchema,
  signUpSchema,
  type SignInValues,
  type SignUpValues,
} from "@/platform/auth/schemas";
import { signInWithGoogle, signInWithPassword, signUpWithPassword } from "@/platform/auth/api";
import { HeroScene } from "@/platform/ui/illustrations/hero-scene";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";

const supabaseConfigured = Boolean(
  import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY,
);

export function SignInPage() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<SignInValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: SignInValues) {
    setSubmitting(true);
    try {
      await signInWithPassword(values);
      toast.success("Tudo certo. Carregando seu painel.");
      navigate("/painel", { replace: true });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível entrar.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      heroKicker="Entrar"
      heroTitle="Continue de onde parou."
      heroDescription="Use o email associado à sua conta. Seu progresso fica salvo entre sessões."
    >
      <FormCard
        title="Acessar a conta"
        footer={
          <>
            Ainda não tem conta?{" "}
            <Link to="/cadastro" className="link font-semibold">
              Comece pelo diagnóstico
            </Link>
          </>
        }
      >
        {!supabaseConfigured ? <UnconfiguredBanner /> : null}
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <Field
            label="Email"
            error={form.formState.errors.email?.message}
            input={
              <Input
                type="email"
                autoComplete="email"
                placeholder="voce@exemplo.com"
                {...form.register("email")}
              />
            }
          />
          <Field
            label="Senha"
            error={form.formState.errors.password?.message}
            input={
              <Input
                type="password"
                autoComplete="current-password"
                placeholder="Mínimo de 6 caracteres"
                {...form.register("password")}
              />
            }
          />
          <Button type="submit" size="lg" className="w-full" loading={submitting} disabled={!supabaseConfigured}>
            Entrar
            <ArrowUpRight className="size-4" aria-hidden />
          </Button>
        </form>
        <OrDivider />
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={async () => {
            try {
              await signInWithGoogle();
            } catch (err) {
              toast.error(err instanceof Error ? err.message : "Falha no login Google.");
            }
          }}
          disabled={!supabaseConfigured}
        >
          <GoogleIcon />
          Continuar com Google
        </Button>
        <p className="mt-5 text-center text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          Esqueceu a senha?{" "}
          <Link to="/recuperar" className="link font-semibold">
            Recuperar acesso
          </Link>
        </p>
      </FormCard>
    </AuthShell>
  );
}

export function SignUpPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const intentTenant = params.get("tenant") === "usp" ? "usp" : "einstein";
  const isUsp = intentTenant === "usp";
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { full_name: "", email: "", password: "" },
  });

  async function onSubmit(values: SignUpValues) {
    setSubmitting(true);
    try {
      const data = await signUpWithPassword(values, { intentTenant });
      if (data.session) {
        // Sessão já criada (sem confirmação por email) — manda direto pro
        // checkout pra cadastrar cartão (cartão obrigatório antes do trial).
        toast.success("Conta criada. Escolha seu plano para começar.");
        navigate(`/precos?tenant=${intentTenant}`, { replace: true });
      } else {
        toast.success("Enviamos um código de 6 dígitos para seu email.");
        navigate(
          `/verificar?email=${encodeURIComponent(values.email)}&tenant=${intentTenant}`,
          { replace: true },
        );
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível criar a conta.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      heroKicker={isUsp ? "Começar · FuvestPro USP" : "Começar · PsiEinstein"}
      heroTitle={
        isUsp ? (
          <>
            Engenharia reversa da{" "}
            <span className="text-gradient-accent">Fuvest.</span>
          </>
        ) : (
          <>
            Crie sua conta para o <span className="text-gradient-accent">diagnóstico.</span>
          </>
        )
      }
      heroDescription="Três campos. 7 dias grátis em qualquer plano. Cartão obrigatório, cobrança só ao fim do trial."
    >
      <FormCard
        title="Criar conta"
        footer={
          <>
            Já tem cadastro?{" "}
            <Link to="/entrar" className="link font-semibold">
              Entrar
            </Link>
          </>
        }
      >
        {!supabaseConfigured ? <UnconfiguredBanner /> : null}
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <Field
            label="Seu nome"
            error={form.formState.errors.full_name?.message}
            input={
              <Input
                type="text"
                autoComplete="name"
                placeholder="Como você gostaria de ser chamada"
                {...form.register("full_name")}
              />
            }
          />
          <Field
            label="Email"
            error={form.formState.errors.email?.message}
            input={
              <Input
                type="email"
                autoComplete="email"
                placeholder="voce@exemplo.com"
                {...form.register("email")}
              />
            }
          />
          <Field
            label="Senha"
            error={form.formState.errors.password?.message}
            hint="Use uma senha de ao menos 6 caracteres que você consiga lembrar."
            input={
              <Input
                type="password"
                autoComplete="new-password"
                placeholder="Mínimo de 6 caracteres"
                {...form.register("password")}
              />
            }
          />
          <Button type="submit" size="lg" className="w-full" loading={submitting} disabled={!supabaseConfigured}>
            Criar conta e começar
            <ArrowUpRight className="size-4" aria-hidden />
          </Button>
        </form>
        <OrDivider />
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className="w-full"
          onClick={async () => {
            try {
              await signInWithGoogle({ intentTenant });
            } catch (err) {
              toast.error(err instanceof Error ? err.message : "Falha no login Google.");
            }
          }}
          disabled={!supabaseConfigured}
        >
          <GoogleIcon />
          Continuar com Google
        </Button>
        <p className="mt-5 text-center text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
          Ao criar conta, você aceita os{" "}
          <Link to="/termos" className="link font-medium">
            Termos
          </Link>{" "}
          e a{" "}
          <Link to="/privacidade" className="link font-medium">
            Política de privacidade
          </Link>
          .
        </p>
      </FormCard>
    </AuthShell>
  );
}

function AuthShell({
  heroKicker,
  heroTitle,
  heroDescription,
  children,
}: {
  heroKicker: string;
  heroTitle: React.ReactNode;
  heroDescription: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="relative min-h-[calc(100vh-56px)] overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-32 -top-20 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="pink"
        className="pointer-events-none absolute -right-32 bottom-0 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-10 top-20 w-16 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-12 md:py-20">
        <div className="grid items-center gap-12 lg:grid-cols-[1fr_1fr]">
          {/* Coluna esquerda — hero copy + ilustração */}
          <div className="animate-fade-up hidden lg:block">
            <Badge variant="accentSolid" className="w-fit">
              <Sparkles className="size-3" aria-hidden />
              {heroKicker}
            </Badge>
            <h1 className="mt-6 font-display text-[length:var(--text-display-lg)] font-semibold leading-[var(--text-display-lg--line-height)] tracking-[var(--text-display-lg--letter-spacing)] text-balance">
              {heroTitle}
            </h1>
            <p className="mt-5 text-[length:var(--text-body-lg)] text-[color:var(--color-muted-foreground)] leading-[var(--text-body-lg--line-height)] text-pretty max-w-md">
              {heroDescription}
            </p>
            <HeroScene className="mt-10 w-[420px] max-w-full" />
          </div>

          {/* Coluna direita — form card */}
          <div className="animate-fade-up [animation-delay:120ms] mx-auto w-full max-w-md">
            {/* Header mobile (até lg) */}
            <div className="mb-8 lg:hidden text-center">
              <Badge variant="accentSolid" className="mx-auto w-fit">
                <Sparkles className="size-3" aria-hidden />
                {heroKicker}
              </Badge>
              <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
                {heroTitle}
              </h1>
            </div>
            {children}
          </div>
        </div>
      </Container>
    </div>
  );
}

function FormCard({
  title,
  children,
  footer,
}: {
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] shadow-[var(--shadow-md)]">
      <div className="border-b border-[--color-border] bg-[color:var(--color-paper)] px-7 py-5">
        <h2 className="font-display text-[length:var(--text-h3)] font-semibold tracking-[var(--text-h3--letter-spacing)]">
          {title}
        </h2>
        {footer ? (
          <p className="mt-1 text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
            {footer}
          </p>
        ) : null}
      </div>
      <div className="p-7">{children}</div>
    </div>
  );
}

function Field({
  label,
  input,
  error,
  hint,
}: {
  label: string;
  input: React.ReactNode;
  error?: string;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {input}
      {error ? (
        <p className="flex items-center gap-1.5 text-[length:var(--text-caption)] text-[color:var(--color-danger)]">
          <AlertCircle className="size-3.5" aria-hidden />
          {error}
        </p>
      ) : hint ? (
        <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">{hint}</p>
      ) : null}
    </div>
  );
}

function OrDivider() {
  return (
    <div className="my-6 flex items-center gap-3">
      <span className="h-px flex-1 bg-[color:var(--color-border)]" />
      <span className="text-[length:var(--text-caption)] uppercase tracking-[var(--text-overline--letter-spacing)] text-[color:var(--color-muted-foreground)]">
        ou
      </span>
      <span className="h-px flex-1 bg-[color:var(--color-border)]" />
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="size-4" aria-hidden>
      <path
        d="M21.6 12.227c0-.708-.063-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.89-1.74 2.982-4.305 2.982-7.351z"
        fill="#4285F4"
      />
      <path
        d="M12 22c2.7 0 4.964-.895 6.618-2.422l-3.232-2.51c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.122H3.064v2.59A9.996 9.996 0 0012 22z"
        fill="#34A853"
      />
      <path
        d="M6.405 13.9A6.005 6.005 0 016.09 12c0-.659.114-1.3.316-1.9V7.51H3.064A9.996 9.996 0 002 12c0 1.614.386 3.14 1.064 4.49l3.341-2.59z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.977c1.468 0 2.786.505 3.823 1.495l2.868-2.868C16.96 2.99 14.695 2 12 2A9.996 9.996 0 003.064 7.51l3.341 2.59C7.19 7.736 9.395 5.977 12 5.977z"
        fill="#EA4335"
      />
    </svg>
  );
}

function UnconfiguredBanner() {
  return (
    <div
      role="status"
      className="mb-6 rounded-md border border-[--color-warning]/30 bg-[color:var(--color-warning-soft)] p-4 text-[length:var(--text-caption)] text-[color:var(--color-warning)]"
    >
      <p className="font-semibold">Supabase não configurado neste ambiente.</p>
      <p className="mt-1 text-[color:var(--color-warning)]/80">
        Preencha <span className="font-mono">VITE_SUPABASE_URL</span> e{" "}
        <span className="font-mono">VITE_SUPABASE_ANON_KEY</span> em{" "}
        <span className="font-mono">.env.local</span>.
      </p>
    </div>
  );
}
